/**
 * CTR Combo Buy Box — Section JavaScript
 * - Gallery (Swiper main slider synced with thumbnails)
 * - Purchase mode radios (Subscribe & Save vs One-Time Purchase)
 * - Accordion open/close (single-open behavior)
 * - UGC strip prev/next scroll
 * - "Buy Once" button (subscription-only mode): adds to cart without a
 *   selling plan, then redirects straight to /cart.
 */

(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* Gallery                                                             */
  /* ------------------------------------------------------------------ */

  class CtrComboBuyBoxGallery {
    constructor(section) {
      this.section = section;
      this.mainSlider = null;
      this.thumbSlider = null;
      this.thumbEl = null;
      this.onThumbClick = this.onThumbClick.bind(this);
      this.init();
    }

    init() {
      if (typeof Swiper === 'undefined') return;

      const thumbEl = this.section.querySelector('[data-cbb-thumb-slider]');
      const mainEl = this.section.querySelector('[data-cbb-main-slider]');
      if (!mainEl) return;

      if (thumbEl && thumbEl.swiper) thumbEl.swiper.destroy(true, true);
      if (mainEl.swiper) mainEl.swiper.destroy(true, true);

      if (thumbEl) {
        this.thumbEl = thumbEl;
        this.thumbSlider = new Swiper(thumbEl, {
          spaceBetween: 12,
          slidesPerView: 4,
          watchOverflow: true,
        });
      }

      this.mainSlider = new Swiper(mainEl, {
        spaceBetween: 0,
        slidesPerView: 1,
        autoHeight: true,
        navigation: {
          nextEl: this.section.querySelector('[data-cbb-main-next]'),
          prevEl: this.section.querySelector('[data-cbb-main-prev]'),
        },
      });

      this.syncThumbActive(this.mainSlider.activeIndex || 0);
      this.mainSlider.on('slideChange', () => {
        this.syncThumbActive(this.mainSlider.activeIndex);
      });

      if (this.thumbEl) {
        this.thumbEl.addEventListener('click', this.onThumbClick);
      }
    }

    getThumbSlides() {
      if (!this.thumbEl) return [];
      return Array.from(this.thumbEl.querySelectorAll('.swiper-slide'));
    }

    onThumbClick(event) {
      if (!this.mainSlider || !this.thumbEl) return;

      const slide = event.target.closest('.swiper-slide');
      if (!slide || !this.thumbEl.contains(slide)) return;

      const index = this.getThumbSlides().indexOf(slide);
      if (index < 0) return;

      this.mainSlider.slideTo(index);
      this.syncThumbActive(index);
    }

    syncThumbActive(activeIndex) {
      this.getThumbSlides().forEach((slide, index) => {
        slide.classList.toggle('swiper-slide-thumb-active', index === activeIndex);
      });

      if (this.thumbSlider && typeof this.thumbSlider.slideTo === 'function') {
        this.thumbSlider.slideTo(activeIndex);
      }
    }

    destroy() {
      if (this.thumbEl) {
        this.thumbEl.removeEventListener('click', this.onThumbClick);
        this.thumbEl = null;
      }
      if (this.mainSlider) {
        this.mainSlider.destroy(true, true);
        this.mainSlider = null;
      }
      if (this.thumbSlider) {
        this.thumbSlider.destroy(true, true);
        this.thumbSlider = null;
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Purchase mode radios                                                */
  /* ------------------------------------------------------------------ */

  function bindPurchaseCards(section) {
    const radios = section.querySelectorAll('[data-cbb-purchase-radio]');
    if (!radios.length || section.dataset.cbbPurchaseBound === 'true') return;
    section.dataset.cbbPurchaseBound = 'true';

    const sellingPlanInput = section.querySelector('[data-cbb-selling-plan-input]');
    const ctaButton = section.querySelector('.ctr-combo-buy-box__cta');

    const sync = () => {
      let selectedPlan = '';

      radios.forEach((radio) => {
        const card = radio.closest('[data-cbb-purchase-card]');
        if (card) card.classList.toggle('is-selected', radio.checked);
        if (radio.checked) selectedPlan = radio.dataset.sellingPlan || '';
      });

      if (sellingPlanInput) {
        sellingPlanInput.value = selectedPlan;
        sellingPlanInput.disabled = selectedPlan === '';
      }

      // Optionally reflect the selection on the CTA button's data attribute,
      // so custom CSS/analytics can react to the current purchase mode.
      if (ctaButton) {
        ctaButton.dataset.purchaseOption = selectedPlan ? 'subscribe' : 'onetime';
      }
    };

    radios.forEach((radio) => radio.addEventListener('change', sync));
    sync();
  }

  /* ------------------------------------------------------------------ */
  /* Accordions                                                          */
  /* ------------------------------------------------------------------ */

  function bindAccordions(section) {
    const container = section.querySelector('[data-cbb-accordions]');
    if (!container || container.dataset.cbbAccordionBound === 'true') return;
    container.dataset.cbbAccordionBound = 'true';

    const accordions = Array.from(container.querySelectorAll('.ctr-combo-buy-box__accordion'));
    const prefersReduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const DURATION = 320;

    const clearPanelTransition = (panel) => {
      panel.style.height = '';
      panel.style.transition = '';
    };

    const openAccordion = (accordion, animate) => {
      const panel = accordion.querySelector('[data-cbb-accordion-panel]');
      accordion.open = true;
      if (!panel) return;

      if (!animate || prefersReduced()) {
        clearPanelTransition(panel);
        return;
      }

      panel.style.height = '0px';
      panel.offsetHeight;
      panel.style.transition = `height ${DURATION}ms ease`;
      panel.style.height = panel.scrollHeight + 'px';

      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'height') return;
        clearPanelTransition(panel);
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
    };

    const closeAccordion = (accordion, animate) => {
      if (!accordion.open) return;
      const panel = accordion.querySelector('[data-cbb-accordion-panel]');

      if (!panel || !animate || prefersReduced()) {
        accordion.open = false;
        if (panel) clearPanelTransition(panel);
        return;
      }

      panel.style.height = panel.scrollHeight + 'px';
      panel.offsetHeight;
      panel.style.transition = `height ${DURATION}ms ease`;
      panel.style.height = '0px';

      const onEnd = (event) => {
        if (event.target !== panel || event.propertyName !== 'height') return;
        accordion.open = false;
        clearPanelTransition(panel);
        panel.removeEventListener('transitionend', onEnd);
      };
      panel.addEventListener('transitionend', onEnd);
    };

    accordions.forEach((accordion) => {
      const summary = accordion.querySelector('summary');
      const panel = accordion.querySelector('[data-cbb-accordion-panel]');
      if (!summary) return;

      if (accordion.open && panel) {
        clearPanelTransition(panel);
      }

      summary.addEventListener('click', (event) => {
        event.preventDefault();

        if (accordion.open) {
          closeAccordion(accordion, true);
          return;
        }

        accordions.forEach((other) => {
          if (other !== accordion) closeAccordion(other, true);
        });
        openAccordion(accordion, true);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* UGC strip                                                           */
  /* ------------------------------------------------------------------ */

  function bindUgcTrack(section) {
    const track = section.querySelector('[data-cbb-ugc-track]');
    if (!track || track.dataset.cbbUgcBound === 'true') return;
    track.dataset.cbbUgcBound = 'true';

    const items = Array.from(track.querySelectorAll('.ctr-combo-buy-box__ugc-item'));
    const prevBtn = section.querySelector('[data-cbb-ugc-prev]');
    const nextBtn = section.querySelector('[data-cbb-ugc-next]');
    const dotsRoot = section.querySelector('[data-cbb-ugc-dots]');

    const scrollStep = () => {
      const item = items[0];
      if (!item) return track.clientWidth * 0.8;
      return item.getBoundingClientRect().width + 8;
    };

    const getActiveIndex = () => {
      if (!items.length) return 0;
      const trackLeft = track.getBoundingClientRect().left;
      let closest = 0;
      let closestDistance = Number.POSITIVE_INFINITY;

      items.forEach((item, index) => {
        const distance = Math.abs(item.getBoundingClientRect().left - trackLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closest = index;
        }
      });

      return closest;
    };

    const updateDots = () => {
      if (!dotsRoot) return;
      const activeIndex = getActiveIndex();
      dotsRoot.querySelectorAll('[data-cbb-ugc-dot]').forEach((dot, index) => {
        const isActive = index === activeIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    };

    const updateNavState = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft >= maxScroll - 2;

      if (prevBtn) {
        prevBtn.disabled = atStart;
        prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false');
      }
      if (nextBtn) {
        nextBtn.disabled = atEnd || maxScroll <= 0;
        nextBtn.setAttribute('aria-disabled', atEnd || maxScroll <= 0 ? 'true' : 'false');
      }

      updateDots();
    };

    if (dotsRoot && items.length > 1) {
      dotsRoot.replaceChildren();
      items.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'ctr-combo-buy-box__ugc-dot';
        dot.dataset.cbbUgcDot = String(index);
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', `Go to video ${index + 1}`);
        dot.addEventListener('click', () => {
          items[index].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        });
        dotsRoot.appendChild(dot);
      });
    } else if (dotsRoot) {
      dotsRoot.replaceChildren();
    }

    if (prevBtn) {
      prevBtn.addEventListener('click', () => {
        track.scrollBy({ left: -scrollStep(), behavior: 'smooth' });
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener('click', () => {
        track.scrollBy({ left: scrollStep(), behavior: 'smooth' });
      });
    }

    track.addEventListener('scroll', updateNavState, { passive: true });
    window.addEventListener('resize', updateNavState);
    updateNavState();

    track.querySelectorAll('[data-cbb-ugc-play]').forEach((button) => {
      button.addEventListener('click', () => {
        const media = button.closest('.ctr-combo-buy-box__ugc-media');
        const video = media ? media.querySelector('[data-cbb-ugc-video]') : null;
        if (!media || !video) return;

        track.querySelectorAll('.ctr-combo-buy-box__ugc-media.is-playing').forEach((other) => {
          if (other === media) return;
          const otherVideo = other.querySelector('[data-cbb-ugc-video]');
          if (otherVideo) {
            otherVideo.pause();
            otherVideo.currentTime = 0;
          }
          other.classList.remove('is-playing');
        });

        media.classList.add('is-playing');
        video.muted = true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {
            media.classList.remove('is-playing');
          });
        }
      });
    });

    track.querySelectorAll('[data-cbb-ugc-video]').forEach((video) => {
      video.addEventListener('click', () => {
        const media = video.closest('.ctr-combo-buy-box__ugc-media');
        if (!media || !media.classList.contains('is-playing')) return;
        video.pause();
        media.classList.remove('is-playing');
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /* Buy Once (subscription-only mode)                                   */
  /* ------------------------------------------------------------------ */

  function bindBuyOnce(section) {
    const button = section.querySelector('[data-cbb-buy-once]');
    if (!button || button.dataset.cbbBuyOnceBound === 'true') return;
    button.dataset.cbbBuyOnceBound = 'true';

    button.addEventListener('click', async (event) => {
      event.preventDefault();
      if (button.disabled || button.classList.contains('is-loading')) return;

      const form = section.querySelector('[data-type="add-to-cart-form"]');
      const productForm = form ? form.closest('ctr-product-form') : null;
      const variantId = form ? form.querySelector('[name="id"]')?.value : null;
      if (!variantId) return;

      const atcBehavior =
        (productForm && productForm.dataset.atcBehavior) ||
        section.dataset.atcBehavior ||
        'direct_checkout';
      const addUrl =
        (window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js';
      const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

      button.classList.add('is-loading');
      button.setAttribute('aria-disabled', 'true');

      try {
        if (atcBehavior === 'direct_checkout' && typeof window.ctrBeginDirectCheckout === 'function') {
          await window.ctrBeginDirectCheckout();
        }

        const response = await fetch(addUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1,
                // No selling_plan — this makes it a one-time purchase.
              },
            ],
          }),
        });

        if (!response.ok) {
          const errorBody = await response.json().catch(() => null);
          throw new Error((errorBody && errorBody.description) || 'Could not add to cart');
        }

        const discountCode = (button.dataset.discountCode || '').trim();
        if (discountCode && typeof window.ctrApplyDiscountCode === 'function') {
          await window.ctrApplyDiscountCode(discountCode);
        }

        if (atcBehavior === 'direct_checkout') {
          if (typeof window.ctrGoToCheckout === 'function') {
            window.ctrGoToCheckout(discountCode);
          } else {
            window.location.href = '/checkout';
          }
          return;
        }

        window.location.href = cartUrl;
      } catch (error) {
        console.error('[ctr-combo-buy-box] Buy once failed:', error);
        button.classList.remove('is-loading');
        button.removeAttribute('aria-disabled');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Init                                                                 */
  /* ------------------------------------------------------------------ */

  function initSection(section) {
    if (!section) return;

    if (section.cbbGallery) {
      section.cbbGallery.destroy();
      section.cbbGallery = null;
    }

    section.cbbGallery = new CtrComboBuyBoxGallery(section);
    bindPurchaseCards(section);
    bindAccordions(section);
    bindUgcTrack(section);
    bindBuyOnce(section);
  }

  function initAll() {
    document.querySelectorAll('.ctr-combo-buy-box').forEach(initSection);
  }

  function boot() {
    if (typeof Swiper !== 'undefined') {
      initAll();
      return;
    }

    let tries = 0;
    const timer = setInterval(() => {
      tries += 1;
      if (typeof Swiper !== 'undefined') {
        clearInterval(timer);
        initAll();
      } else if (tries > 40) {
        clearInterval(timer);
      }
    }, 50);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.ctr-combo-buy-box') || event.target.closest('.ctr-combo-buy-box');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target.querySelector('.ctr-combo-buy-box') || event.target.closest('.ctr-combo-buy-box');
    if (section && section.cbbGallery) {
      section.cbbGallery.destroy();
      section.cbbGallery = null;
    }
  });
})();
